import React, { useState, useEffect } from 'react'
import Papa from 'papaparse'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GroupAdd as GroupAddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { usersAPI, agentsAPI } from '../services/api'
import ISO_639_1_LANGUAGES from '../utils/languages'
import SearchableSelect from '../components/SearchableSelect'

const LANGUAGE_OPTIONS = ISO_639_1_LANGUAGES.map(({ code, name, nativeName }) => ({
  value: code,
  label: `${nativeName} (${name})`,
}))
import AdminPageWrapper from '../components/AdminPageWrapper'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const AdminUsersPage = () => {
  const [users, setUsers] = useState([])
  const [agents, setAgents] = useState([])
  const [availableAppRoles, setAvailableAppRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  const [emailFilter, setEmailFilter] = useState('')
  const [nameFilter, setNameFilter] = useState('')

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  // Create-user form
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserLanguage, setNewUserLanguage] = useState('en')
  const [newUserAgents, setNewUserAgents] = useState([])

  // Edit-user form (single consolidated dialog)
  const [editUserName, setEditUserName] = useState('')
  const [editUserLanguage, setEditUserLanguage] = useState('en')
  const [editUserApplicationRoles, setEditUserApplicationRoles] = useState([])
  const [editUserAgents, setEditUserAgents] = useState([])

  // bulk-add dialog
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false)
  const [bulkCsvHeaders, setBulkCsvHeaders] = useState([])
  const [bulkCsvRows, setBulkCsvRows] = useState([])
  const [bulkEmailColumn, setBulkEmailColumn] = useState('')
  const [bulkAppRoles, setBulkAppRoles] = useState([])
  const [bulkAgents, setBulkAgents] = useState([])
  const [bulkLanguage, setBulkLanguage] = useState('en')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkResults, setBulkResults] = useState(null)
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 })

  useEffect(() => {
    loadUsers()
    loadAgents()
    loadAvailableApplicationRoles()
  }, [page, pageSize, emailFilter, nameFilter])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const filters = {}
      if (emailFilter) filters.email_filter = emailFilter
      if (nameFilter) filters.name_filter = nameFilter

      const response = await usersAPI.listUsers(filters, page + 1, pageSize)
      setUsers(response.users || [])
      setTotalCount(response.pagination?.total || 0)
    } catch (err) {
      console.error('Failed to load users:', err)
      setError('Failed to load users: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  const loadAgents = async () => {
    try {
      // Use minimal flag to only fetch agent names, not full schemas
      const response = await agentsAPI.getAgents(true)
      setAgents(response.agents || [])
    } catch (err) {
      console.error('Failed to load agents:', err)
    }
  }

  const loadAvailableApplicationRoles = async () => {
    try {
      const response = await usersAPI.getAvailableApplicationRoles()
      setAvailableAppRoles(response.application_roles || [])
    } catch (err) {
      console.error('Failed to load application roles:', err)
    }
  }

  const handleCreateUser = async () => {
    try {
      setError(null)
      await usersAPI.createUser({
        email: newUserEmail,
        assigned_agents: newUserAgents,
        preferred_language: newUserLanguage,
      })
      setCreateDialogOpen(false)
      setNewUserEmail('')
      setNewUserLanguage('en')
      setNewUserAgents([])
      loadUsers()
    } catch (err) {
      console.error('Failed to create user:', err)
      setError('Failed to create user: ' + (err.response?.data?.detail || err.message))
    }
  }

  // Returns true if two arrays contain the same values (order-insensitive).
  const sameMembers = (a, b) => a.length === b.length && a.every((v) => b.includes(v))

  const handleEditUser = async () => {
    try {
      setError(null)
      const trimmedName = (editUserName || '').trim()
      const originalName = (selectedUser.name || '').trim()
      const updates = {}
      if (trimmedName !== originalName) {
        // null clears the name; the backend accepts None for the field
        updates.name = trimmedName || null
      }
      if (editUserLanguage !== (selectedUser.preferred_language || 'en')) {
        updates.preferred_language = editUserLanguage
      }
      if (!sameMembers(editUserApplicationRoles, selectedUser.application_roles || [])) {
        updates.application_roles = editUserApplicationRoles
      }
      if (!sameMembers(editUserAgents, selectedUser.assigned_agents || [])) {
        updates.assigned_agents = editUserAgents
      }
      if (Object.keys(updates).length > 0) {
        await usersAPI.updateUser(selectedUser.email, updates)
      }
      setEditDialogOpen(false)
      setSelectedUser(null)
      loadUsers()
    } catch (err) {
      console.error('Failed to update user:', err)
      setError('Failed to update user: ' + (err.response?.data?.detail || err.message))
    }
  }

  const handleDeleteUser = async () => {
    try {
      setError(null)
      await usersAPI.deleteUser(selectedUser.email)
      setDeleteDialogOpen(false)
      setSelectedUser(null)
      loadUsers()
    } catch (err) {
      console.error('Failed to delete user:', err)
      setError('Failed to delete user: ' + (err.response?.data?.detail || err.message))
    }
  }

  const openEditDialog = (user) => {
    setSelectedUser(user)
    setEditUserName(user.name || '')
    setEditUserLanguage(user.preferred_language || 'en')
    setEditUserApplicationRoles(user.application_roles || [])
    setEditUserAgents(user.assigned_agents || [])
    setEditDialogOpen(true)
  }

  const openDeleteDialog = (user) => {
    setSelectedUser(user)
    setDeleteDialogOpen(true)
  }

  const handleBulkFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setBulkCsvHeaders(result.meta.fields || [])
        setBulkCsvRows(result.data)
        setBulkEmailColumn('')
        setBulkResults(null)
      },
    })
  }

  const BULK_CHUNK_SIZE = 50

  const handleBulkSubmit = async () => {
    if (!bulkEmailColumn || bulkCsvRows.length === 0) return
    const emails = bulkCsvRows
      .map((row) => (row[bulkEmailColumn] || '').trim())
      .filter(Boolean)
    if (emails.length === 0) {
      setError('No valid email addresses found in the selected column.')
      return
    }

    const chunks = []
    for (let i = 0; i < emails.length; i += BULK_CHUNK_SIZE) {
      chunks.push(emails.slice(i, i + BULK_CHUNK_SIZE))
    }

    try {
      setBulkLoading(true)
      setBulkProgress({ current: 0, total: chunks.length })

      const aggregated = { results: [], summary: { created: 0, updated: 0, skipped: 0 } }

      for (let i = 0; i < chunks.length; i++) {
        const response = await usersAPI.bulkUpsertUsers({
          emails: chunks[i],
          application_roles: bulkAppRoles,
          assigned_agents: bulkAgents,
          preferred_language: bulkLanguage,
        })
        aggregated.results.push(...response.results)
        aggregated.summary.created += response.summary.created
        aggregated.summary.updated += response.summary.updated
        aggregated.summary.skipped += response.summary.skipped
        setBulkProgress({ current: i + 1, total: chunks.length })
      }

      setBulkResults(aggregated)
      loadUsers()
    } catch (err) {
      setError('Bulk add failed: ' + (err.response?.data?.detail || err.message))
    } finally {
      setBulkLoading(false)
    }
  }

  const resetBulkDialog = () => {
    setBulkCsvHeaders([])
    setBulkCsvRows([])
    setBulkEmailColumn('')
    setBulkAppRoles([])
    setBulkAgents([])
    setBulkLanguage('en')
    setBulkLoading(false)
    setBulkResults(null)
    setBulkProgress({ current: 0, total: 0 })
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  if (loading && users.length === 0) {
    return <LoadingSpinner />
  }

  return (
    <AdminPageWrapper title="User Management">
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, gap: 1 }}>
        <Tooltip title="Refresh users">
          <IconButton onClick={loadUsers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create User
        </Button>
        <Button
          variant="outlined"
          startIcon={<GroupAddIcon />}
          onClick={() => { resetBulkDialog(); setBulkAddDialogOpen(true) }}
        >
          Bulk Add
        </Button>
      </Box>

      {error && (
        <ErrorAlert
          message={error}
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        />
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Email Filter"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <TextField
            label="Name Filter"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
          />
        </Box>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Token Roles</TableCell>
              <TableCell>Application Roles</TableCell>
              <TableCell>Assigned Agents</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Manually Added</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.name || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.roles?.length > 0 ? (
                      user.roles.map((role) => (
                        <Chip key={role} label={role} size="small" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.application_roles?.length > 0 ? (
                      user.application_roles.map((role) => (
                        <Chip key={role} label={role} size="small" color="secondary" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.assigned_agents?.length > 0 ? (
                      user.assigned_agents.map((agent) => (
                        <Chip key={agent} label={agent} size="small" color="primary" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{user.preferred_language || '-'}</TableCell>
                <TableCell>
                  {user.last_login_time
                    ? new Date(user.last_login_time).toLocaleString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {user.manually_added ? (
                    <Chip label="Yes" size="small" color="warning" />
                  ) : (
                    <Chip label="No" size="small" />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Edit user">
                    <IconButton size="small" onClick={() => openEditDialog(user)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete user">
                    <IconButton size="small" onClick={() => openDeleteDialog(user)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      {users.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No users found. Create a user to get started.
        </Alert>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <SearchableSelect
            label="Preferred Language"
            value={newUserLanguage}
            onChange={(e) => setNewUserLanguage(e.target.value)}
            options={LANGUAGE_OPTIONS}
            formControlProps={{ sx: { mb: 2, mt: 1 } }}
          />
          <FormControl fullWidth>
            <InputLabel>Assigned Agents</InputLabel>
            <Select
              multiple
              value={newUserAgents}
              onChange={(e) => setNewUserAgents(e.target.value)}
              input={<OutlinedInput label="Assigned Agents" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.name} value={agent.name}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={!newUserEmail}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog (consolidated) */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {selectedUser?.email}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={editUserName}
            onChange={(e) => setEditUserName(e.target.value)}
            inputProps={{ maxLength: 200 }}
            helperText="Display name shown across the application"
            sx={{ mb: 2, mt: 1 }}
          />
          <SearchableSelect
            label="Preferred Language"
            value={editUserLanguage}
            onChange={(e) => setEditUserLanguage(e.target.value)}
            options={LANGUAGE_OPTIONS}
            formControlProps={{ sx: { mb: 2 } }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Application Roles</InputLabel>
            <Select
              multiple
              value={editUserApplicationRoles}
              onChange={(e) => setEditUserApplicationRoles(e.target.value)}
              input={<OutlinedInput label="Application Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" color="secondary" />
                  ))}
                </Box>
              )}
            >
              {availableAppRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assigned Agents</InputLabel>
            <Select
              multiple
              value={editUserAgents}
              onChange={(e) => setEditUserAgents(e.target.value)}
              input={<OutlinedInput label="Assigned Agents" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" color="primary" />
                  ))}
                </Box>
              )}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.name} value={agent.name}>
                  {agent.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Application roles persist across logins and supplement or replace token-based
            roles (AgentFlow.Admin, AgentFlow.User).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{selectedUser?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Add Users Dialog */}
      <Dialog open={bulkAddDialogOpen} onClose={() => { resetBulkDialog(); setBulkAddDialogOpen(false) }} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Add Users</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV. Pick the email column, then select the roles and agents to assign.
            Existing users will have their roles and agents replaced.
          </Typography>

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
            {bulkCsvRows.length > 0 ? `${bulkCsvRows.length} rows loaded — click to change` : 'Choose CSV file'}
            <input type="file" accept=".csv" hidden onChange={handleBulkFileChange} />
          </Button>

          {bulkCsvHeaders.length > 0 && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Email Column</InputLabel>
                <Select
                  value={bulkEmailColumn}
                  onChange={(e) => setBulkEmailColumn(e.target.value)}
                  input={<OutlinedInput label="Email Column" />}
                >
                  {bulkCsvHeaders.map((h) => (
                    <MenuItem key={h} value={h}>{h}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Application Roles</InputLabel>
                <Select
                  multiple
                  value={bulkAppRoles}
                  onChange={(e) => setBulkAppRoles(e.target.value)}
                  input={<OutlinedInput label="Application Roles" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" color="secondary" />)}
                    </Box>
                  )}
                >
                  {availableAppRoles.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Assigned Agents</InputLabel>
                <Select
                  multiple
                  value={bulkAgents}
                  onChange={(e) => setBulkAgents(e.target.value)}
                  input={<OutlinedInput label="Assigned Agents" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" color="primary" />)}
                    </Box>
                  )}
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.name} value={agent.name}>{agent.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <SearchableSelect
                label="Preferred Language"
                value={bulkLanguage}
                onChange={(e) => setBulkLanguage(e.target.value)}
                options={LANGUAGE_OPTIONS}
                formControlProps={{ sx: { mb: 2 } }}
              />
            </>
          )}

          {bulkLoading && bulkProgress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Processing chunk {bulkProgress.current} of {bulkProgress.total}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(bulkProgress.current / bulkProgress.total) * 100}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          {bulkResults && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {bulkResults.summary.created} created · {bulkResults.summary.updated} updated · {bulkResults.summary.skipped} skipped
              </Typography>
              {bulkResults.results.filter((r) => r.status === 'skipped').length > 0 && (
                <List dense disablePadding>
                  {bulkResults.results
                    .filter((r) => r.status === 'skipped')
                    .map((r) => (
                      <ListItem key={r.email} disablePadding>
                        <ListItemText
                          primary={r.email || '(blank)'}
                          secondary={r.detail}
                          primaryTypographyProps={{ color: 'error' }}
                        />
                      </ListItem>
                    ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAddDialogOpen(false)}>Close</Button>
          {!bulkResults && (
            <Button
              onClick={handleBulkSubmit}
              variant="contained"
              disabled={!bulkEmailColumn || bulkCsvRows.length === 0 || bulkLoading}
              startIcon={bulkLoading ? <CircularProgress size={16} /> : null}
            >
              {bulkLoading ? 'Processing…' : 'Submit'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminUsersPage
